import { Bell, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useNotifications } from "@/hooks/use-notifications";
import { useNavigate, useLocation } from "react-router-dom";

export const NotificationDropdown = () => {
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAllAsRead,
  } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation();

  const displayCount = unreadCount > 9 ? "9+" : unreadCount;

  const handleOpen = () => {
    fetchNotifications();
  };

  const handleClick = (notif: any) => {
    const teamId = notif.metadata?.teamId;

    if (location.pathname !== "/teams") {
      navigate("/teams");
    }
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && handleOpen()}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 rounded-full"
        >
          <Bell className="w-5 h-5" />

          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 text-xs bg-red-500 text-white rounded-full px-1.5 min-w-[18px] text-center">
              {displayCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[calc(100vw-2rem)] p-0 overflow-hidden border-border/50 bg-card/95 backdrop-blur-sm sm:w-80"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
          <span className="font-semibold text-sm">Notifications</span>

          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-primary hover:underline"
            >
              Read all
            </button>
          )}
        </div>

        {/* CONTENT */}
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No new notifications
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {notifications.map((notif) => (
                <div
                  key={notif._id}
                  onClick={() => handleClick(notif)}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-background/50 hover:bg-background/80 transition cursor-pointer"
                >
                  {/* ICON */}
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>

                  {/* CONTENT */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium truncate">
                        {notif.title}
                      </p>

                      <Badge variant="secondary" className="text-xs">
                        New
                      </Badge>
                    </div>

                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notif.message}
                    </p>

                    <p className="text-[10px] text-muted-foreground mt-1">
                      {new Date(notif.createdAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
